import { useEffect, useMemo, useState } from 'react'

import type { FlightsClassTypes } from 'constants/flight'
import Places from 'constants/places'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import useOffers from 'hooks/Offers/useOffers'
import useStreamEnded from 'hooks/Offers/useStreamEnded'
import useOfferListKey from 'hooks/Offers/useOfferListKey'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { getIsEnabledFlightsBundledLuxPlusPricing } from 'luxPlus/selectors/featureToggle'
import { canRequestFlightLuxPlusPricing } from 'lib/flights/flightLuxPlusPricing'

export interface UseMaldivesDirectHotelStreamSearchParams {
  checkInDate: string | undefined
  checkOutDate: string | undefined
  rooms: Array<App.Occupants>
  fareClassForSearch: FlightsClassTypes
  holidayTypes: Array<string> | undefined
  amenities: Array<string> | undefined
  sortBy: App.OfferListSortByOptions | undefined
  isTourBundleMode: boolean
  feederOriginAirportCode?: string
  charterNumberOfNights?: number
}

export interface MaldivesDirectHotelStreamSearch {
  bundleFilters: App.OfferListFilters
  listKey: string
  maldivesOfferIds: Array<string>
  maldivesOfferCount: number
  offers: Array<App.HotelOffer>
  isOffersLoading: boolean
  hasMoreStreamingOffers: boolean
  canStreamHotelOffers: boolean
  minRequiredOffers: number
  setOfferPage: (page: number) => void
  pageSize: number
}

export function getMaldivesDirectHotelSearchDates(checkInDate: string | undefined, checkOutDate: string | undefined): {
  checkIn: string
  checkOut: string
  canStream: boolean
} {
  const hasPartialDateSelection = Boolean(checkInDate) !== Boolean(checkOutDate)

  return {
    checkIn: checkInDate ?? '',
    checkOut: checkOutDate ?? '',
    canStream: !hasPartialDateSelection,
  }
}

/**
 * Hotel streaming search state for Maldives Direct: selected dates, bundle filters,
 * Redux offer list slice, loading flags, and scroll pagination (minRequiredOffers).
 */
export function useMaldivesDirectHotelStreamSearch(
  params: UseMaldivesDirectHotelStreamSearchParams,
): MaldivesDirectHotelStreamSearch {
  const {
    checkInDate,
    checkOutDate,
    rooms,
    fareClassForSearch,
    holidayTypes,
    amenities,
    sortBy,
    isTourBundleMode,
    feederOriginAirportCode,
    charterNumberOfNights,
  } = params

  const pageSize = SEARCH_STREAMING_PAGE_SIZE
  const [offerPage, setOfferPage] = useState(1)
  const minRequiredOffers = offerPage * pageSize
  const bundledLuxPlusPricingEnabled = useAppSelector(getIsEnabledFlightsBundledLuxPlusPricing)
  const requestLuxPlusPricing = useAppSelector(state => canRequestFlightLuxPlusPricing(state, {
    enabled: bundledLuxPlusPricingEnabled,
  }))

  // A date-less search must not turn the cheapest charter flight into an availability filter.
  const hotelSearchDates = getMaldivesDirectHotelSearchDates(checkInDate, checkOutDate)
  const { checkIn: effectiveCheckIn, checkOut: effectiveCheckOut, canStream: canStreamHotelOffers } = hotelSearchDates

  const bundleFilters = useMemo<App.OfferListFilters>(() => {
    const filters = buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: effectiveCheckIn,
      checkOut: effectiveCheckOut,
      rooms: rooms,
      fareClass: fareClassForSearch as App.OfferListFilters['fareClass'],
      feederOriginAirportCode,
      charterNumberOfNights,
    })
    const { limit: _, ...filtersWithoutLimit } = filters
    return {
      ...filtersWithoutLimit,
      holidayTypes,
      amenities,
      sortBy: sortBy || undefined,
      isLuxPlusMember: requestLuxPlusPricing || undefined,
    }
  }, [effectiveCheckIn, effectiveCheckOut, rooms, fareClassForSearch, feederOriginAirportCode, charterNumberOfNights, holidayTypes, amenities, sortBy, requestLuxPlusPricing])

  const offerListKey = useOfferListKey(bundleFilters)
  const listKey = canStreamHotelOffers ? offerListKey : ''

  useEffect(() => {
    setOfferPage(1)
  }, [listKey])

  const maldivesOfferList = useAppSelector(state => listKey ? state.offer.offerLists[listKey] : undefined)
  const maldivesOfferIds = maldivesOfferList?.offerIds ?? EmptyArray as Array<string>
  const maldivesOfferCount = maldivesOfferList?.offerCount ?? 0
  const streamEnded = useStreamEnded(bundleFilters, { disabled: !canStreamHotelOffers })
  const [offersMaybe] = useOffers<App.HotelOffer>(maldivesOfferIds)
  const offers = useMemo<Array<App.HotelOffer>>(() => nonNullable(offersMaybe), [offersMaybe])

  const isWaitingForSelectedDates = !canStreamHotelOffers

  const isOffersLoading = useMemo(() => {
    if (!canStreamHotelOffers) {
      return isWaitingForSelectedDates
    }
    return !streamEnded && maldivesOfferIds.length === 0
  }, [canStreamHotelOffers, isWaitingForSelectedDates, streamEnded, maldivesOfferIds.length])

  const hasMoreStreamingOffers = useMemo(
    () => !isTourBundleMode && canStreamHotelOffers && (!streamEnded || maldivesOfferIds.length > minRequiredOffers),
    [isTourBundleMode, canStreamHotelOffers, streamEnded, maldivesOfferIds.length, minRequiredOffers],
  )

  return {
    bundleFilters,
    listKey,
    maldivesOfferIds,
    maldivesOfferCount,
    offers,
    isOffersLoading,
    hasMoreStreamingOffers,
    canStreamHotelOffers,
    minRequiredOffers,
    setOfferPage,
    pageSize,
  }
}
